/**
 * SlashCommandMenu - Claude-Code-style command palette for the chat composer.
 *
 * Purely presentational: renders the (already admin-filtered) command list.
 * Placement is handled by the Radix Popover it's rendered inside (portaled +
 * collision-aware, so it flips above/below and escapes the composer card's
 * overflow clip - see ChatComposer). The active row is driven by the composer's
 * keyboard handling (the textarea keeps focus and owns ↑/↓/Enter/Esc). Commands
 * are DATA from SLASH_COMMANDS (@protoss/shared); adding a command never touches
 * this component (CLAUDE.md "Reusable primitives").
 */

import type { SlashCommandCategory, SlashCommandDefinition } from '@protoss/shared';
import { SLASH_COMMAND_CATEGORIES } from '@protoss/shared';
import { Pill } from '@protoss/ui';
import { Fragment } from 'react';
import { STAGE_PILL_VARIANT } from './ComposerPicker';

const CATEGORY_LABELS: Record<SlashCommandCategory, string> = Object.fromEntries(
  SLASH_COMMAND_CATEGORIES.map((c) => [c.key, c.label])
) as Record<SlashCommandCategory, string>;

export interface SlashCommandMenuProps {
  commands: SlashCommandDefinition[];
  activeIndex: number;
  onSelect: (command: SlashCommandDefinition) => void;
  onHover: (index: number) => void;
}

export function SlashCommandMenu({
  commands,
  activeIndex,
  onSelect,
  onHover,
}: SlashCommandMenuProps) {
  return (
    <div className="ai-chat-slash-menu" role="listbox" aria-label="Slash commands">
      {commands.length === 0 ? (
        <div className="ai-chat-slash-menu__empty">No matching commands</div>
      ) : (
        commands.map((command, index) => (
          <Fragment key={command.key}>
            {/* `commands` arrives sorted by category (ChatComposer), so a header
                whenever the category changes yields one section per category. */}
            {(index === 0 || commands[index - 1]!.category !== command.category) && (
              <div className="ai-chat-slash-menu__group-label" role="presentation">
                {CATEGORY_LABELS[command.category]}
              </div>
            )}
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={
                index === activeIndex
                  ? 'ai-chat-slash-menu__item ai-chat-slash-menu__item--active'
                  : 'ai-chat-slash-menu__item'
              }
              // onMouseDown (not onClick) so the textarea never loses focus.
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(command);
              }}
              onMouseEnter={() => onHover(index)}
            >
              <span className="ai-chat-slash-menu__label-row">
                <span className="ai-chat-slash-menu__label">{command.label}</span>
                {command.stage && (
                  <Pill
                    variant={STAGE_PILL_VARIANT[command.stage]}
                    className="ai-chat-slash-menu__stage"
                  >
                    {command.stage}
                  </Pill>
                )}
              </span>
              <span className="ai-chat-slash-menu__description">{command.description}</span>
            </button>
          </Fragment>
        ))
      )}
    </div>
  );
}
